import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/navigation/Sidebar';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import QuickActionMenu from '../../components/navigation/QuickActionMenu';
import KPICard from '../admin-dashboard/components/KPICard';
import Icon from '../../components/AppIcon';
import Toaster from '../../components/Toaster';
import TwoFAReminderBanner from '../../components/TwoFAReminderBanner';
import { getUnacknowledgedAlerts } from '../../services/alertService';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

// Skeleton placeholder shown inside each KPI card while data loads
const KPISkeleton = () => (
  <div className="bg-card border border-border rounded-lg p-4 md:p-6">
    <div className="flex items-start justify-between mb-3 md:mb-4">
      <div className="flex-1 space-y-2">
        <div className="h-3 w-28 bg-muted rounded animate-pulse" />
        <div className="h-9 w-32 bg-muted rounded animate-pulse" />
      </div>
      <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-muted animate-pulse" />
    </div>
    <div className="flex items-center justify-between">
      <div className="h-3 w-24 bg-muted rounded animate-pulse" />
      <div className="h-3 w-16 bg-muted rounded animate-pulse" />
    </div>
  </div>
);

const AdminOverview = () => {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [metricsResponse, alertsResult] = await Promise.all([
        supabase.functions.invoke('get-admin-metrics?all=true'),
        getUnacknowledgedAlerts(),
      ]);
      if (metricsResponse?.data?.success) setMetrics(metricsResponse.data.data);
      if (alertsResult?.success) setAlerts(alertsResult?.data);
    } catch (error) {
      console.error('Load overview data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const windowLabel = 'All time';

  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    return `$${parseFloat(value)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatPercentage = (value) => {
    if (!value && value !== 0) return '0%';
    return `${parseFloat(value)?.toFixed(1)}%`;
  };

  const formatNumber = (value) => {
    if (!value && value !== 0) return '0';
    return parseFloat(value)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // kpiData is always defined — skeleton cards render when loading=true
  const kpiData = [
    {
      title: 'Monthly Recurring Revenue',
      value: formatCurrency(metrics?.mrr),
      subtitle: 'MRR',
      trend: metrics?.revenueGrowth > 0 ? 'up' : 'down',
      trendValue: formatPercentage(Math.abs(metrics?.revenueGrowth)),
      icon: 'DollarSign',
      iconColor: 'bg-success',
      tooltip: 'Monthly Recurring Revenue (MRR)\n\nThe total normalised monthly revenue from active paying subscribers.\n\n• Monthly plan subscribers: their last actual charge amount\n• Annual plan subscribers: their last charge ÷ 12\n• Free trial & one-time purchases: excluded\n\nSource: payments table (succeeded subscription/upgrade/downgrade rows), joined to user_subscriptions.',
    },
    {
      title: 'Annual Recurring Revenue',
      value: formatCurrency(metrics?.arr),
      subtitle: 'ARR — annual plans only',
      trend: 'up',
      trendValue: formatPercentage(metrics?.revenueGrowth),
      icon: 'TrendingUp',
      iconColor: 'bg-primary',
      tooltip: 'Annual Recurring Revenue (ARR)\n\nThe total value of active annual subscriptions — sum of the actual lump-sum charges paid by annual-plan subscribers.\n\nMonthly subscribers are intentionally excluded: use MRR × 12 if you want a blended projection.\n\nSource: payments table (succeeded rows for users on annual plan_type).',
    },
    {
      title: 'Marketplace GMV',
      value: formatCurrency(metrics?.gmv),
      subtitle: `${windowLabel} · take rate ${formatPercentage(metrics?.platformTakeRate)}`,
      trend: 'up',
      trendValue: formatPercentage(metrics?.platformTakeRate),
      icon: 'ShoppingCart',
      iconColor: 'bg-warning',
      tooltip: `Gross Merchandise Value (GMV)\n\nTotal dollar value of all expert contracts with status = 'completed' in the selected window (${windowLabel}).\n\nThis is the gross figure — it does NOT deduct platform commission or expert payouts.\n\nPlatform Take Rate = commission earned ÷ GMV × 100.\n\nSource: marketplace_contracts (completed_at within window).`,
    },
    {
      title: 'AI Cost per Report',
      value: `$${parseFloat(metrics?.avgCost || 0)?.toFixed(2)}`,
      subtitle: `P95: $${parseFloat(metrics?.p95Cost || 0)?.toFixed(2)}`,
      trend: 'neutral',
      trendValue: `Max: $${parseFloat(metrics?.maxCost || 0)?.toFixed(2)}`,
      icon: 'Cpu',
      iconColor: 'bg-secondary',
      tooltip: `AI Cost per Report\n\nAverage OpenAI API cost per case that has been through the AI pipeline (${windowLabel}).\n\n• Average: mean cost across all cases with AI runs\n• P95: 95th percentile — only 5% of cases cost more than this\n• Max: single most expensive case\n\nCosts include triage (phase 1), clarifying questions (phase 2), and report generation (phase 3).\n\nSource: ai_runs table (cost_usd column), grouped by case_id.`,
    },
    {
      title: 'Active Subscriptions',
      value: formatNumber(metrics?.activeSubscriptions),
      subtitle: `Total users: ${formatNumber(metrics?.totalUsers)}`,
      trend: 'up',
      trendValue: `+${formatNumber(metrics?.newSignups)}`,
      icon: 'Users',
      iconColor: 'bg-primary',
      tooltip: `Active Subscriptions\n\nCount of user_subscriptions rows where is_active = true. Includes monthly and annual plans — free_trial accounts are active but do not contribute to MRR/ARR.\n\n+N (trend): new user_profiles created in ${windowLabel}.\n\nTotal users: all rows in user_profiles regardless of subscription status.`,
    },
    {
      title: 'Conversion Rate',
      value: formatPercentage(metrics?.conversionRate),
      subtitle: 'Trial to paid',
      trend: 'neutral',
      trendValue: null,
      icon: 'Target',
      iconColor: 'bg-success',
      tooltip: 'Trial-to-Paid Conversion Rate\n\nPercentage of users who started a free trial and later upgraded to a paid plan.\n\nFormula: converted users ÷ (converted + still-on-trial) × 100\n\nA user is counted as converted when subscription_history contains a plan_changed event with old_plan_type = free_trial → monthly or annual.\n\nChurn Rate: cancellations in the last 30 days ÷ currently active subscriptions × 100.',
    },
    {
      title: 'Customer LTV',
      value: formatCurrency(metrics?.ltv),
      subtitle: `ARPU: ${formatCurrency(metrics?.arpu)} / mo`,
      trend: 'neutral',
      trendValue: null,
      icon: 'Award',
      iconColor: 'bg-primary',
      tooltip: 'Customer Lifetime Value (LTV)\n\nEstimated total revenue from an average subscriber before they cancel.\n\nFormula: ARPU ÷ Monthly Churn Rate\n\n• ARPU (Average Revenue Per User) = MRR ÷ active subscriber count\n• Monthly Churn Rate = cancellations in last 30d ÷ active subscriptions\n\nExample: $49 ARPU ÷ 5% churn = $980 LTV\n\nShows $0 when churn rate is 0 (can\'t divide by zero — no cancellations yet means LTV is theoretically infinite).',
    },
    {
      title: 'Cases Created',
      value: formatNumber(metrics?.casesCreated),
      subtitle: windowLabel,
      trend: 'up',
      trendValue: `${formatNumber(metrics?.reportsGenerated)} reports`,
      icon: 'FileText',
      iconColor: 'bg-primary',
      tooltip: `Cases Created\n\nTotal number of cases submitted by clients in ${windowLabel} (all statuses including draft).\n\nReports: cases that completed the full AI pipeline and have a generated report.\n\nSource: cases table (created_at), reports table (generated_at).`,
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Toaster position="top-center" />
      <TwoFAReminderBanner />
      <Sidebar userRole="admin" />
      <QuickActionMenu userRole="admin" />
      <div className="lg:ml-60 min-h-screen">
        <header className="sticky top-0 z-40 bg-card border-b border-border">
          <div className="flex items-center justify-between px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  const isCollapsed = document.body.classList.toggle('sidebar-collapsed');
                  localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Toggle sidebar"
              >
                <Icon name="PanelLeft" size={24} />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground">
                  Overview
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Platform KPIs at a glance
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter userRole="admin" />
              <div className="hidden md:flex items-center gap-3 pl-3 border-l border-border">
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">Admin User</p>
                  <p className="text-xs text-muted-foreground">System Administrator</p>
                </div>
                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
                  <Icon name="Shield" size={20} color="#FFFFFF" />
                </div>
                <button
                  onClick={async () => { await signOut(); navigate('/login'); }}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
                >
                  <Icon name="LogOut" size={16} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-foreground">Key Performance Indicators</h2>
            <button
              onClick={loadData}
              className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="RefreshCw" size={16} />
              Refresh
            </button>
          </div>

          {/* ── Destination Charges Info ── */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="ShieldCheck" size={18} color="var(--color-success)" />
              <h2 className="text-base font-semibold text-foreground">Payment Architecture</h2>
              <span className="text-xs text-muted-foreground ml-1">(Stripe Destination Charges)</span>
            </div>

            <div className="flex items-start gap-3 bg-success/5 border border-success/30 rounded-lg px-4 py-3 mb-4">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" className="mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-success">No platform escrow liability</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Client payments go directly to expert Connect accounts via destination charges. Platform only receives application fees.
                  Expert payouts are held via manual payout schedules — the platform never holds escrowed funds.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Active Escrows (Held)</span>
                  <div className="w-8 h-8 rounded-full bg-warning/10 flex items-center justify-center">
                    <Icon name="Lock" size={16} color="var(--color-warning)" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-foreground">{formatCurrency(metrics?.escrowHeld ?? 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">Source: <code className="text-[10px]">marketplace_contracts.escrow_amount</code> where <code className="text-[10px]">escrow_status = 'held'</code> — set by Stripe webhook on payment confirmation. Not final until client approves.</p>
              </div>

              <div className="bg-card border border-border rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Platform Fees Earned</span>
                  <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                    <Icon name="DollarSign" size={16} color="var(--color-success)" />
                  </div>
                </div>
                <p className="text-2xl font-bold text-success">{formatCurrency(metrics?.platformFees ?? 0)}</p>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-success/15 text-success border border-success/30">
                    <Icon name="ShieldCheck" size={10} />
                    safe to withdraw
                  </span>
                  <p className="text-xs text-muted-foreground">Released contracts only · held &amp; refunded excluded</p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-3 px-1">
              <Icon name="Info" size={13} color="var(--color-muted-foreground)" />
              <p className="text-xs text-muted-foreground">Expert Connect accounts use <strong>manual payouts</strong>. Bank transfers require the expert to request a payout after completing KYC.</p>
            </div>
          </div>

          {/* ── Money Flow / Cashflow Ledger ── */}
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <Icon name="ArrowLeftRight" size={18} color="var(--color-primary)" />
              <h2 className="text-base font-semibold text-foreground">Money Flow</h2>
              <span className="text-xs text-muted-foreground ml-1">(All-time cumulative, from local DB)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

              {/* Inflows */}
              <div className="bg-card border border-success/40 rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Inflows</span>
                  <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                    <Icon name="ArrowDownLeft" size={16} color="var(--color-success)" />
                  </div>
                </div>
                {loading ? <div className="h-8 w-24 bg-muted rounded animate-pulse" /> : (
                  <p className="text-2xl font-bold text-success">{formatCurrency(metrics?.totalInflows ?? 0)}</p>
                )}
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Destination charges</span>
                    <span className="font-medium">{formatCurrency(metrics?.escrowInflows ?? 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Subscriptions</span>
                    <span className="font-medium">{formatCurrency(metrics?.subscriptionInflows ?? 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Credits & other</span>
                    <span className="font-medium">{formatCurrency(metrics?.otherInflows ?? 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Platform fees</span>
                    <span className="font-medium">{formatCurrency(metrics?.platformFees ?? 0)}</span>
                  </div>
                </div>
              </div>

              {/* Outflows */}
              <div className="bg-card border border-error/40 rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Outflows</span>
                  <div className="w-8 h-8 rounded-full bg-error/10 flex items-center justify-center">
                    <Icon name="ArrowUpRight" size={16} color="var(--color-error)" />
                  </div>
                </div>
                {loading ? <div className="h-8 w-24 bg-muted rounded animate-pulse" /> : (
                  <p className="text-2xl font-bold text-error">{formatCurrency(metrics?.totalOutflows ?? 0)}</p>
                )}
                <div className="mt-2 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Refunds to clients</span>
                    <span className="font-medium text-error">{formatCurrency(metrics?.refundOutflows ?? 0)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Expert payouts</span>
                    <span className="font-medium text-error">{formatCurrency(metrics?.expertPayoutOutflows ?? 0)}</span>
                  </div>
                </div>
              </div>

              {/* Net + Commission */}
              <div className="bg-card border border-primary/40 rounded-lg p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Platform Revenue</span>
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="TrendingUp" size={16} color="var(--color-primary)" />
                  </div>
                </div>
                {loading ? <div className="h-8 w-24 bg-muted rounded animate-pulse" /> : (
                  <p className="text-2xl font-bold text-primary">{formatCurrency(metrics?.platformFees ?? 0)}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">Application fees from destination charges</p>
                <div className="mt-2 space-y-1 border-t border-border pt-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-muted-foreground">Subscription revenue</span>
                    <span className="font-medium text-success">{formatCurrency(metrics?.subscriptionRevenue ?? 0)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground italic mt-1">Platform keeps application_fee_amount from each destination charge</p>
                </div>
              </div>

            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
            {loading
              ? kpiData.map((_, i) => <KPISkeleton key={i} />)
              : kpiData.map((kpi, index) => <KPICard key={index} {...kpi} />)
            }
          </div>

        </main>
      </div>
    </div>
  );
};

export default AdminOverview;
